import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Button, Modal } from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";

// Component Imports
import { SystemHealth } from "../components/SystemHealth";
import { SystemWaterUsage } from "../components/SystemWaterUsage";
import { FlowHealthWidget } from "../components/FlowHealth";
import { TotalConsumption } from "../components/TotalConsumption";
import { AverageAccountability } from "../components/AverageAccountability";
import { AnomalyDetectionCard } from "../components/AnomalyDetectionCard";
import { CapacityPlanning } from "../components/CapacityPlanning";
import { TrendChart } from "../components/TrendChart";
import { LeakFlow } from "../components/LeakFlow";
import DraggableWrapper from "../components/DraggableDashboard/DraggableWrapper";
import { useTheme } from "../context/ThemeContext";

// Logic & Context Imports
import { get, post } from "../Libraries/Ajax/HttpService";
import { useDistrict } from "../utils/DistrictContext";
import * as htmlToImage from "html-to-image";
import {
  getSystemBattery,
  getFlowHealth,
  getTotalConsumption,
  getCapacityPlanning,
  getWidgetLayout,
  editWidgetLayout,
} from "../services";
// --- ADD THIS ABOVE YOUR Home() FUNCTION ---
const EmptyRowDropZone = ({ row, onDrop, isEditMode, colors }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "CARD", // MUST match the type in DraggableWrapper's useDrag
    canDrop: () => true, // Explicitly tell the browser this is a valid spot
    drop: (item) => {
      onDrop(item.row, row, item.index, 0);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  if (!isEditMode) return null;

  return (
    <div
      ref={drop}
      className="w-full min-h-[150px] border-2 border-dashed rounded-xl flex items-center justify-center transition-all"
      style={{
        borderColor: isOver ? colors.primary : `${colors.border}80`,
        backgroundColor: isOver ? `${colors.primary}20` : "transparent",
        cursor: canDrop ? "copy" : "no-drop", // Force a valid cursor
        zIndex: 50, // Ensure it stays above the chart background
        position: "relative",
      }}
    >
      <p
        style={{
          color: colors.textSecondary,
          fontWeight: "bold",
          pointerEvents: "none",
        }}
      >
        {isOver ? "Release to Drop" : `↓ Drag here to move to ${row} ↓`}
      </p>
    </div>
  );
};
function Home() {
  const navigate = useNavigate();
  const selectedDistrict = useDistrict();
  const domEl = useRef(null);

  // Mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [showWidgetSelector, setShowWidgetSelector] = useState(false);

  // Data state
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemCommunication, setSystemCommunication] = useState(null);
  const [systemDevice, setSystemDevice] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [selected, setSelected] = useState("Last Month");
  const [percentage, setPercentage] = useState(null);
  const [arrayData, setArrayData] = useState(null);
  const [last, setLast] = useState(null);
  const [next, setNext] = useState(null);
  const [color, setColor] = useState([]);
  const [accountability, setAccountability] = useState(null);
  const [yearlyConsumption, setYearlyConsumption] = useState(null);
  const [flowHealthData, setFlowHealthData] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState("gallons");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optionone, setOptionone] = useState(null);
  const [optiontwo, setOptiontwo] = useState(null);
  const [optionthree, setOptionthree] = useState(false);
  const [optionfour, setOptionfour] = useState(true);
  const [thisr, setThisr] = useState(null);
  const [trendRow, setTrendRow] = useState([]);
  const { theme, colors } = useTheme();
  const [globalRange, setGlobalRange] = useState("Past Month");
  const timeOptions = [
    { label: "D", value: "Past Day" },
    { label: "W", value: "Past Week" },
    { label: "1M", value: "Past Month" },
    { label: "3M", value: "Past 3 Months" },
    { label: "Y", value: "Past Year" },
  ];

  // Card state - matching your original structure
  const [topRowCards, setTopRowCards] = useState([
    { id: "system-health" },
    { id: "system-water-usage" },
    { id: "flow-health" },
  ]);

  const [middleRowCards, setMiddleRowCards] = useState([
    { id: "anomaly-detection" },
    { id: "average-accountability" },
    { id: "capacity-planning" },
  ]);

  const [bottomRowCards, setBottomRowCards] = useState([
    { id: "total-consumption" },
    { id: "leak-flow" },
  ]);

  // Available widgets
  const availableWidgets = [
    { id: "system-health", label: "System Health", row: "top" },
    { id: "system-water-usage", label: "System Water Usage", row: "top" },
    { id: "flow-health", label: "Flow Health", row: "top" },
    { id: "total-consumption", label: "Total Consumption", row: "bottom" },
    {
      id: "average-accountability",
      label: "Average Accountability",
      row: "middle",
    },
    { id: "capacity-planning", label: "Capacity Planning", row: "middle" },
    { id: "anomaly-detection", label: "Anomaly Detection", row: "middle" },
    { id: "leak-flow", label: "Leak Flow", row: "bottom" },

    { id: "trend", label: "Trend Chart", row: "trend" },
  ];

  const widgetIdMap = {
    systemHealth: "system-health",
    waterUsage: "system-water-usage",
    flowHealth: "flow-health",
    anomalyDetection: "anomaly-detection",
    averageAccountability: "average-accountability",
    capacityPlanning: "capacity-planning",
    totalConsumption: "total-consumption",
    leakFlow: "leak-flow",
    trend: "trend",
  };

  const mapLayoutToRows = (widgets) => {
    const top = [],
      middle = [],
      bottom = [],
      trend = [];

    widgets
      .sort((a, b) => (a.layout.x || 0) - (b.layout.x || 0))
      .forEach((widget) => {
        const mappedId = widgetIdMap[widget.id];
        if (!mappedId || !widget.layout) return;

        // CHANGE: If it's the trend chart, force it into the trend array
        if (mappedId === "trend") {
          trend.push({ id: "trend" });
          return;
        }

        const y = widget.layout.y;
        if (y < 4) top.push({ id: mappedId });
        else if (y < 8) middle.push({ id: mappedId });
        else bottom.push({ id: mappedId });
      });

    return { top, middle, bottom, trend };
  };
  // 1. Create the reverse map (API key -> UI ID)
  const reverseWidgetIdMap = Object.fromEntries(
    Object.entries(widgetIdMap).map(([key, value]) => [value, key]),
  );

  /**
   * Constructs the payload for the API by converting the current
   * UI state (arrays) back into grid coordinates.
   */
  const buildLayoutPayload = () => {
    const widgets = [];

    const processRow = (cards, yStart) => {
      cards.forEach((card, index) => {
        const apiId = reverseWidgetIdMap[card.id];
        if (!apiId) return;

        widgets.push({
          id: apiId,
          layout: {
            x: index * 4,
            y: yStart, // This ensures if 'trend' is in row 0, it saves as y: 0
            w: card.id === "trend" ? 12 : 4, // Keep width logic
            h: 4,
          },
        });
      });
    };

    processRow(topRowCards, 0);
    processRow(middleRowCards, 4);
    processRow(bottomRowCards, 8);
    processRow(trendRow, 12); // Process the trend row just like the others

    return widgets;
  };

  const moveCard = useCallback(
    (fromRow, toRow, dragIndex, hoverIndex) => {
      if (fromRow === toRow && dragIndex === hoverIndex) return;

      const setters = {
        top: setTopRowCards,
        middle: setMiddleRowCards,
        bottom: setBottomRowCards,
        trend: setTrendRow,
      };

      const lists = {
        top: [...topRowCards],
        middle: [...middleRowCards],
        bottom: [...bottomRowCards],
        trend: [...trendRow],
      };

      const [movedItem] = lists[fromRow].splice(dragIndex, 1);

      // If moving to trend row, just push it to the end
      if (toRow === "trend") {
        lists[toRow].push(movedItem);
      } else {
        lists[toRow].splice(hoverIndex, 0, movedItem);
      }

      setters[fromRow](lists[fromRow]);
      if (fromRow !== toRow) {
        setters[toRow](lists[toRow]);
      }
    },
    [topRowCards, middleRowCards, bottomRowCards, trendRow],
  );

  useEffect(() => {
    if (!selectedDistrict) return; // prevent unnecessary calls

    const fetchSystemBattery = async () => {
      try {
        const res = await getSystemBattery();
        const data = res?.data;
        setSystemHealth(data?.comm_status);
        setSystemCommunication(data?.comm_status);
        setSystemData(data?.data_status);
        setSystemDevice(data?.device_status);
        setNext(data?.next);
        setLast(data?.last);
      } catch (err) {
        console.error("System battery fetch failed:", err);
      }
    };

    fetchSystemBattery();
  }, [selectedDistrict]);

  useEffect(() => {
    const fetchFlowHealth = async () => {
      try {
        const res = await getFlowHealth();
        setFlowHealthData(res?.data?.flow_data_per || res?.flow_data_per);
      } catch (err) {
        console.error("Flow health fetch failed:", err);
      }
    };
    fetchFlowHealth();
  }, [selectedDistrict]);

  useEffect(() => {
    const fetchData = async () => {
      const res = await getTotalConsumption();
      if (res?.data?.status) {
        setYearlyConsumption(res.data.consumption);
      }
    };
    fetchData();
  }, [selectedDistrict]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getCapacityPlanning(selectedUnit);
        if (res?.data?.status) {
          setApiData(res.data);
        }
      } catch (err) {
        console.error("Capacity API error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedUnit, selectedDistrict]);

  useEffect(() => {
    const fetchLayout = async () => {
      try {
        const res = await getWidgetLayout();

        if (res?.status && res.widgets) {
          const { top, middle, bottom, trend } = mapLayoutToRows(res.widgets);

          // ✅ Default case (no layout)
          if (!res.widgets.length) {
            setTopRowCards([
              { id: "system-health" },
              { id: "system-water-usage" },
              { id: "flow-health" },
            ]);

            setMiddleRowCards([
              { id: "anomaly-detection" },
              { id: "average-accountability" },
              { id: "capacity-planning" },
            ]);

            setBottomRowCards([
              { id: "total-consumption" },
              { id: "leak-flow" },
            ]);

            setTrendRow([{ id: "trend" }]);
          } else {
            setTopRowCards(top);
            setMiddleRowCards(middle);
            setBottomRowCards(bottom);
            setTrendRow(trend.length ? trend : []);
          }

          if (res.message) {
            toast.info(res.message);
          }
        }
      } catch (err) {
        console.error("Widget layout fetch failed:", err);
      }
    };

    fetchLayout();
  }, [selectedDistrict]);

  // Helper Functions
  const downloadImage = async () => {
    const dataUrl = await htmlToImage.toPng(domEl.current);
    const link = document.createElement("a");
    link.download = `Dashboard-${dayjs().format("YYYY-MM-DD")}.png`;
    link.href = dataUrl;
    link.click();
  };

  const refreshClick = () => {
    post(`apis/manual_refresh?manual=start`).then((response) => {
      toast.info(response?.data?.msg);
    });
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    window.location.reload();
  };

  const handleSaveLayout = async () => {
    try {
      const payload = buildLayoutPayload();

      const res = await editWidgetLayout(payload);

      if (res?.status) {
        toast.success(res.message || "Layout saved successfully");
        setIsEditMode(false);
      } else {
        toast.error("Failed to save layout");
      }
    } catch (err) {
      console.error("Save layout error:", err);
      toast.error("Something went wrong");
    }
  };

  const handleAddWidget = (widgetId) => {
    const widget = availableWidgets.find((w) => w.id === widgetId);
    if (!widget) return;

    switch (widget.row) {
      case "top":
        setTopRowCards([...topRowCards, { id: widgetId }]);
        break;
      case "middle":
        setMiddleRowCards([...middleRowCards, { id: widgetId }]);
        break;
      case "bottom":
        setBottomRowCards([...bottomRowCards, { id: widgetId }]);
        break;
      case "trend":
        setTrendRow([{ id: "trend" }]);
        break;
    }
    setShowWidgetSelector(false);
  };

  const removeWidget = (id) => {
    // Find which row contains the widget
    if (topRowCards.some((card) => card.id === id)) {
      setTopRowCards(topRowCards.filter((card) => card.id !== id));
    } else if (middleRowCards.some((card) => card.id === id)) {
      setMiddleRowCards(middleRowCards.filter((card) => card.id !== id));
    } else if (bottomRowCards.some((card) => card.id === id)) {
      setBottomRowCards(bottomRowCards.filter((card) => card.id !== id));
    } else if (trendRow.some((card) => card.id === id)) {
      setTrendRow(trendRow.filter((card) => card.id !== id));
    }
  };

  // Render widget based on id
  const renderWidget = (id) => {
    switch (id) {
      case "system-health":
        return (
          <SystemHealth
            onClick={(tab) => navigate("/health", { state: { tab } })}
            data={{
              systemHealth,
              systemCommunication,
              systemData,
              systemDevice,
              last,
              next,
            }}
            loading={systemHealth === null}
          />
        );
      case "system-water-usage":
        return (
          <SystemWaterUsage
            onOptionChange={(e) => setSelected(e.target.value)}
            data={{
              currentFlow: optionone,
              previousFlow: optiontwo,
              percentage,
              isDecrease: optionfour,
              fillPercentage: optionthree,
              dateRange: thisr,
              selectedOption: selected,
            }}
            globalRange={globalRange}
          />
        );
      case "flow-health":
        return <FlowHealthWidget data={flowHealthData} />;
      case "total-consumption":
        return (
          <TotalConsumption
            data={yearlyConsumption}
            selectedDistrict={selectedDistrict?.selectedDistrict}
            globalRange={globalRange}
          />
        );
      case "average-accountability":
        return (
          <AverageAccountability
            data={accountability}
            globalRange={globalRange}
          />
        );
      case "capacity-planning":
        return (
          <CapacityPlanning
            data={apiData}
            loading={loading}
            selectedUnit={selectedUnit}
            onUnitChange={(unit) => setSelectedUnit(unit)}
          />
        );
      case "anomaly-detection":
        return <AnomalyDetectionCard globalRange={globalRange} />;
      case "leak-flow":
        return <LeakFlow />;
      case "trend":
        return (
          <TrendChart
            data={arrayData}
            colors={color}
            parameterone={new Date().getFullYear() - 1}
            parametertwo={new Date().getFullYear()}
            onRefresh={refreshClick}
            onDownload={downloadImage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className="min-h-screen px-4 max-[567px]:px-2 overflow-hidden mb-4"
        ref={domEl}
      >
        {/* Header with title and controls */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>
            Dashboard
          </h1>
          <div className="flex gap-3 flex-wrap">
            <div
              className="flex items-center px-1 py-0.5 rounded-lg border"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {/* <span className="text-[10px] px-2 font-bold opacity-50 uppercase">Global:</span> */}
              {timeOptions.map((tp) => (
                <button
                  key={tp.value}
                  onClick={() => setGlobalRange(tp.value)}
                  className="px-2.5 py-1 rounded-md text-[13px] font-black transition-all cursor-pointer"
                  style={{
                    backgroundColor:
                      globalRange === tp.value ? colors.primary : "transparent",
                    color:
                      globalRange === tp.value ? "#fff" : colors.textSecondary,
                  }}
                >
                  {tp.label}
                </button>
              ))}
            </div>
            {!isEditMode ? (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEditClick}
                style={{
                  backgroundImage: colors.gradient,
                  color: "#fff",
                  border: "none",
                }}
                className="hover:opacity-90"
              >
                Edit Dashboard
              </Button>
            ) : (
              <>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setShowWidgetSelector(true)}
                  style={{
                    background: colors.cardBg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                  className="hover:opacity-90"
                >
                  Add Widget
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleCancelEdit}
                  style={{
                    background: "transparent",
                    color: colors.textSecondary,
                    border: `1px solid ${colors.border}`,
                  }}
                  className="hover:opacity-80"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveLayout}
                  style={{
                    backgroundImage: colors.gradient,
                    color: "#fff",
                    border: "none",
                  }}
                  className="hover:opacity-90"
                >
                  Save Layout
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Example for TOP ROW - Apply the same pattern to MIDDLE and BOTTOM */}
        <div
          className="flex flex-wrap gap-6 mb-6 min-h-[50px] relative"
          style={{ width: "100%" }}
        >
          {topRowCards.map((card, index) => (
            <div
              key={card.id}
              // This class makes the card take equal space.
              // min-w-[45%] ensures 2 per row on large screens, max-w-full lets it expand.
              className="flex-1 min-w-[300px] max-[1400px]:min-w-full"
            >
              <DraggableWrapper
                id={card.id}
                index={index}
                row="top"
                moveCard={moveCard}
                isDragMode={isEditMode}
                onRemove={isEditMode ? removeWidget : null}
              >
                {renderWidget(card.id)}
              </DraggableWrapper>
            </div>
          ))}
          {topRowCards.length === 0 && isEditMode && (
            <EmptyRowDropZone
              row="top"
              onDrop={moveCard}
              isEditMode={isEditMode}
              colors={colors}
            />
          )}
        </div>

        <div
          // Change grid to flex and add wrap
          className="flex flex-wrap gap-6 mb-6 min-h-[50px] relative"
          style={{ width: "100%" }}
        >
          {middleRowCards
            .filter(
              (card) =>
                card.id !== "capacity-planning" ||
                selectedDistrict?.selectedDistrict === "GPMD",
            )
            .map((card, index) => (
              <div
                key={card.id}
                /**
                 * flex-1: allows the card to grow and fill empty space.
                 * min-w-[45%]: ensures on desktop they sit side-by-side (2 or 3 per row).
                 * max-w-full: handles mobile responsiveness.
                 */
                className="flex-1 min-w-[350px] max-w-full"
              >
                <DraggableWrapper
                  id={card.id}
                  index={index}
                  row="middle"
                  moveCard={moveCard}
                  isDragMode={isEditMode}
                  onRemove={isEditMode ? removeWidget : null}
                >
                  {renderWidget(card.id)}
                </DraggableWrapper>
              </div>
            ))}

          {/* Ensure the DropZone takes full width if the row is empty */}
          {middleRowCards.length === 0 && isEditMode && (
            <div className="w-full">
              <EmptyRowDropZone
                row="middle"
                onDrop={moveCard}
                isEditMode={isEditMode}
                colors={colors}
              />
            </div>
          )}
        </div>

        <div
          // Change "grid" to "flex flex-wrap" to allow dynamic width filling
          className="flex flex-wrap gap-4 md:gap-6 mb-4 md:mb-6 min-h-[50px] relative"
          style={{ width: "100%" }}
        >
          {bottomRowCards.map((card, index) => (
            <div
              key={card.id}
              /**
               * flex-1: allows cards to grow and fill empty space automatically.
               * min-w-[300px]: ensures they don't get too small.
               * max-w-full: handles responsiveness on smaller screens.
               */
              className="flex-1 min-w-[300px] max-w-full"
            >
              <DraggableWrapper
                id={card.id}
                index={index}
                row="bottom"
                moveCard={moveCard}
                isDragMode={isEditMode}
                onRemove={isEditMode ? removeWidget : null}
              >
                {renderWidget(card.id)}
              </DraggableWrapper>
            </div>
          ))}

          {/* Ensure the DropZone fills the row if it's empty */}
          {bottomRowCards.length === 0 && isEditMode && (
            <div className="w-full">
              <EmptyRowDropZone
                row="bottom"
                onDrop={moveCard}
                isEditMode={isEditMode}
                colors={colors}
              />
            </div>
          )}
        </div>

        {/* TREND ROW - Dedicated Catch Zone */}
        {/* BOTTOM/TREND AREA */}
        <div
          className="relative mb-20"
          style={{
            minHeight: isEditMode ? "250px" : "auto",
            zIndex: isEditMode ? 100 : 1,
          }}
        >
          <div className="flex flex-wrap gap-4 md:gap-6 min-h-[50px]">
            {trendRow.map((card, index) => (
              <div key={card.id} className="flex-1 min-w-full">
                <DraggableWrapper
                  id={card.id}
                  index={index}
                  row="trend"
                  moveCard={moveCard}
                  isDragMode={isEditMode}
                  onRemove={isEditMode ? removeWidget : null}
                >
                  {renderWidget(card.id)}
                </DraggableWrapper>
              </div>
            ))}
          </div>

          {/* Always show this DropZone at the very bottom when editing */}
          {isEditMode && trendRow.length === 0 && (
            <EmptyRowDropZone
              row="trend"
              onDrop={moveCard}
              isEditMode={isEditMode}
              colors={colors}
            />
          )}
        </div>
        {/* Add Widget Modal */}
        <Modal
          title={<span style={{ color: colors.text }}>Add Widget</span>}
          open={showWidgetSelector}
          onCancel={() => setShowWidgetSelector(false)}
          footer={null}
          width={600}
          closeIcon={<CloseOutlined style={{ color: colors.text }} />}
          styles={{
            content: {
              background: colors.background,
              border: `1px solid ${colors.border}`,
            },
            header: {
              background: "transparent",
              borderBottom: `1px solid ${colors.border}`,
            },
            body: {
              background: "transparent",
            },
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            {availableWidgets
              .filter(
                (w) =>
                  ![
                    ...topRowCards,
                    ...middleRowCards,
                    ...bottomRowCards,
                    ...trendRow,
                  ].some((card) => card.id === w.id),
              )
              .map((widget) => (
                <button
                  key={widget.id}
                  onClick={() => handleAddWidget(widget.id)}
                  className="p-4 rounded-lg transition-all text-left"
                  style={{
                    background: colors.innerCardBg || colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.boxShadow = `0 0 10px ${colors.primary}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div className="font-medium" style={{ color: colors.text }}>
                    {widget.label}
                  </div>
                </button>
              ))}
            {availableWidgets.filter(
              (w) =>
                ![...topRowCards, ...middleRowCards, ...bottomRowCards].some(
                  (card) => card.id === w.id,
                ),
            ).length === 0 && (
              <div
                className="col-span-2 text-center py-8"
                style={{ color: colors.textSecondary }}
              >
                <PlusOutlined className="text-3xl mb-2" />
                <p>All widgets are already added to the dashboard</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </DndProvider>
  );
}

export default Home;
